// src/utils/dev/devHelpers.ts - Development helper functions

/**
 * Check if the application is in development mode
 * @returns Boolean indicating if in development mode
 */
export function isDevMode(): boolean {
	// Check localStorage first (set by healthApi)
	if (typeof window !== 'undefined' && localStorage.getItem('dev_mode') === 'true') {
		return true;
	}

	// Otherwise use Vite's environment variable
	return import.meta.env.DEV || false;
}

/**
 * Check if user has a development token
 * @returns Boolean indicating if a development token exists
 */
export function hasDevToken(): boolean {
	if (typeof window === 'undefined') return false;

	const token = localStorage.getItem('token');
	return !!token && token.includes('dev-mode');
}

/**
 * Log development-only messages (only appear in dev mode)
 * @param message Main message to log
 * @param data Optional data to log
 * @deprecated Use createLogger from $lib/utils/logger instead
 */
export function devLog(message: string, data?: unknown): void {
	// Skip if not in browser environment
	if (typeof window === 'undefined') return;

	// Check if alerts flow debugging is enabled
	const DEBUG_NS = (localStorage.getItem('DEBUG') ?? '')
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean);
	const DEBUG_ALERTS_FLOW = DEBUG_NS.includes('*') || DEBUG_NS.includes('alerts.flow');

	if (!isDevMode() || !DEBUG_ALERTS_FLOW) return;

	// Use native console for dev logging (deprecated - use createLogger instead)
	if (data) {
		// eslint-disable-next-line no-console
		console.log(`🧪 ${message}`, data);
	} else {
		// eslint-disable-next-line no-console
		console.log(`🧪 ${message}`);
	}
}

/**
 * Create a development token and store it
 * @returns The created token
 */
export function createDevToken(): string {
	const token = `dev-mode-mock-token-${Date.now()}`;

	if (typeof window !== 'undefined') {
		localStorage.setItem('token', token);
		localStorage.setItem('token_type', 'bearer');

		// Set expiration 1 hour from now
		const expiresAt = Date.now() + 3600 * 1000;
		localStorage.setItem('token_expires_at', expiresAt.toString());
	}

	return token;
}

/**
 * Clear all development data
 */
export function clearDevData(): void {
	if (typeof window === 'undefined') return;

	localStorage.removeItem('token');
	localStorage.removeItem('token_type');
	localStorage.removeItem('token_expires_at');
	localStorage.removeItem('user_data');
	localStorage.removeItem('permissions');
}
